import React from "react";
import { mergeCourseSteps } from "./mergeCourseSteps";
import {
  COURSE_REQUIRED_DOCS,
  COURSE_TYPES_MAP,
  courseDetailsConfig,
  COURSE_HOUSING_TYPES_MAP,
  LANGUAGES,
} from "../constants";
import {
  IAccommodationFeeViewModel,
  ICourseViewModel,
  IDetailFileFrontEndModel,
  IDetailsViewModel,
  IOtherFeesFrontEndModel,
} from "../types";
import {
  calculateFinancialSupport,
  getBeautifiedDuration,
  toCurrency,
} from "../../../helpers";
import { HTMLTagRegEx } from "../../../constants";
import { calcFeeWithDiscount } from "./helperFunctions";

function getBranchesNames(branches, branchesId) {
  return branchesId.map((b) => branches.find((branch) => branch.id === b).name);
}

export function parseToCreateCoursePreview({ courseRawData, branches }) {
  const courseData = mergeCourseSteps(courseRawData);
  const language = LANGUAGES.find((l) => l.id === courseData.language.id)!.name;

  // Required Documents -------------------------------------
  const selectedRequiredDocNames = courseData.requiredDocs.map((doc) => {
    const existedDoc = COURSE_REQUIRED_DOCS.find((d) => d.name === doc.name);

    return existedDoc?.longName || doc.name;
  });

  const additionalDocuments = Object.values(
    courseData.additionalDocuments || {},
  );
  const additionalDocumentNames = additionalDocuments
    .filter((d) => d.trim())
    .map((d) => d.trim());

  // Application Link ---------------------------------------
  let applicationLink;

  if (courseData.applicationLink) {
    applicationLink = courseData.applicationLink.includes("http", 0)
      ? courseData.applicationLink
      : "http://" + courseData.applicationLink;
  }

  // Housing Type -------------------------------------------
  let housingTypes;

  if (courseData.housingTypes?.length) {
    housingTypes = courseData.housingTypes
      .sort((a, b) => +a - +b)
      .map((type) => COURSE_HOUSING_TYPES_MAP[type]);
  }

  // Restricted Countries -----------------------------------
  let restrictedCountries;

  if (courseData.restrictedCountries?.length) {
    restrictedCountries = courseData.restrictedCountries.map((c) => c.name);
  }

  // Finance --------------------------------------------------
  const currency = +courseData.currency.id;
  const price = +courseData.price;

  const {
    feeWithCurrency: priceWithCurrency,
    calcFeeWithCurrency: calcPriceWithCurrency,
    discountWithCurrency: priceDiscountWithCurrency,
    hasSupport: hasPriceSupport,
  } = calcFeeWithDiscount({
    fee: price,
    hasFeeDiscount: !!courseData.hasPriceDiscount,
    feeDiscountType: courseData.priceDiscountType?.id ?? null,
    feeDiscountAmount: +(courseData.priceDiscountAmount ?? ""),
    currency,
  });

  // Commission
  const commissionAmount = calculateFinancialSupport(
    price,
    true,
    courseData.commissionType.id,
    +courseData.commissionAmount,
  );

  // Accommodation Fees
  const accommodationFees: IAccommodationFeeViewModel[] = [];

  if (courseData.residenceAmount) {
    accommodationFees.push({
      label: "Residence",
      amount: toCurrency(courseData.residenceAmount, currency, " ", "code"),
    });
  }

  if (courseData.homestayAmount) {
    accommodationFees.push({
      label: "Homestay",
      amount: toCurrency(courseData.homestayAmount, currency, " ", "code"),
    });
  }

  if (courseData.insuranceAmount) {
    accommodationFees.push({
      label: "Insurance",
      amount: toCurrency(courseData.insuranceAmount, currency, " ", "code"),
    });
  }

  // Other Fees -----------------------------------------------
  let otherFeesSum = 0;
  const otherFees: IOtherFeesFrontEndModel[] = [];

  [
    ...(courseData.mainOtherFees || []),
    ...(courseData.addedOtherFees || []),
  ].forEach((fee) => {
    if (fee?.name && fee?.amount) {
      otherFees.push({
        name: fee.name,
        amount: toCurrency(fee.amount, currency, " ", "code"),
      });

      otherFeesSum += +fee.amount;
    }
  });

  const {
    feeWithCurrency: otherFeesWithCurrency,
    calcFeeWithCurrency: calcOtherFeesWithCurrency,
    discountWithCurrency: otherFeesDiscountWithCurrency,
    hasSupport: hasOtherFeesSupport,
  } = calcFeeWithDiscount({
    fee: otherFeesSum,
    hasFeeDiscount: !!courseData.hasOtherFeesDiscount,
    feeDiscountType: courseData.otherFeesDiscountType?.id ?? null,
    feeDiscountAmount: +(courseData.otherFeesDiscountAmount ?? ""),
    currency,
  });

  //Details
  const details: IDetailsViewModel[] = [];

  courseDetailsConfig.forEach((item) => {
    if (
      courseData.details[`type_${item.type}`] &&
      (courseData.details[`type_${item.type}`] as string)
        .replace(HTMLTagRegEx, "")
        .trim().length
    ) {
      details.push({
        type: item.type,
        typeName: item.name,
        content: courseData.details[`type_${item.type}`],
      });
    }
  });

  //Detail Files
  const detailFiles: IDetailFileFrontEndModel[] = (
    courseData.detailFiles || []
  ).map((file) => ({
    id: file.id,
    name: file.name!,
    size: file.size!,
    path: file.preview!,
  }));

  const coursePreviewData: ICourseViewModel = {
    language: language,
    category: courseData.category,
    courseType: COURSE_TYPES_MAP[courseData.courseType],
    minAge: courseData.minAge,
    maxAge: courseData.maxAge ?? "N/A",
    branches: getBranchesNames(branches, courseData.branches),
    requiredDocs: [...selectedRequiredDocNames, ...additionalDocumentNames],
    applicationLink: applicationLink,
    applicationForms: courseData.applicationForms,
    courseDuration: getBeautifiedDuration(
      "Course",
      courseData.durationUnit.id,
      courseData.durationAmount,
    ),
    housingTypes: housingTypes,
    restrictedCountries: restrictedCountries,
    price: priceWithCurrency,
    priceDuration: getBeautifiedDuration(
      "Course",
      courseData.priceDurationUnit.id,
      courseData.priceDurationAmount,
    ),
    commissionAmount: toCurrency(commissionAmount, currency, " ", "code"),
    otherFees: otherFees,
    accommodationFees: accommodationFees,
    priceDiscount: priceDiscountWithCurrency,
    calculatedPrice: calcPriceWithCurrency,
    otherFeesSum: otherFeesWithCurrency,
    otherFeesDiscount: otherFeesDiscountWithCurrency,
    calculatedOtherFees: calcOtherFeesWithCurrency,
    hasPriceSupport: hasPriceSupport,
    hasOtherFeesSupport: hasOtherFeesSupport,
    details: details,
    detailFiles: detailFiles,
  };

  return coursePreviewData;
}
